import { useState } from "react";
import { Copy, ListChecks, Save, RotateCcw, X } from "lucide-react";
import ListingTemplateDropdown from "./ListingTemplateDropdown";
import SaveAsTemplateDialog from "./SaveAsTemplateDialog";

/**
 * Feb-2026 (User-Wunsch 27.05.2026 / Überarbeitung Feb-2026) —
 * Harmonisierte Inserate-Toolbar mit drei gleich breiten, optisch identischen
 * Aktionen:
 *  1. „Vorlage übernehmen"     — kopiert eine gespeicherte Vorlage ins Formular
 *  2. „Als Vorlage speichern"  — speichert die aktuellen Stammdaten als Vorlage
 *                                (fahrzeugspezifische Daten werden verworfen)
 *  3. „Nur Pflichtfelder"      — blendet alle optionalen Sektionen aus
 *
 * Der frühere „Geführter Modus"-Toggle wurde aus der Toolbar entfernt — die
 * Steuerung erfolgt jetzt über den „Rundgang"-Button oben im Seitenkopf
 * (standardmäßig aus).
 */
export default function CreateListingToolbar({
    isEdit,
    setForm,
    form,
    essentialOnly,
    setEssentialOnly,
    draftRestoreCandidate,
    discardDraft,
    restoreDraft,
}) {
    const chipBase = "inline-flex items-center justify-center gap-1.5 w-full h-9 px-3 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors";
    const chipActive = "bg-[#FFCC00] text-[#111827] border-[#111827]";
    const chipInactive = "bg-white border-gray-200 text-gray-700 hover:border-gray-400 hover:text-[#111827]";

    const [tplDialogOpen, setTplDialogOpen] = useState(false);

    return (
        <div className="mb-3 space-y-2" data-testid="create-listing-toolbar">
            <div className="bg-white border border-gray-200 rounded-sm p-2.5 flex flex-wrap items-center justify-between gap-3">
                <div className={`grid grid-cols-1 ${isEdit ? "sm:grid-cols-1" : "sm:grid-cols-3"} gap-2 flex-1 min-w-0`}>
                    {!isEdit && <ListingTemplateDropdown setForm={setForm} isEdit={isEdit} fullWidth chipClassName={`${chipBase} ${chipInactive}`} />}
                    {!isEdit && (
                        <button
                            type="button"
                            onClick={() => setTplDialogOpen(true)}
                            className={`${chipBase} ${chipInactive}`}
                            data-testid="save-as-template-btn"
                            title="Aktuelle Stammdaten als wiederverwendbare Vorlage speichern (fahrzeugspezifische Daten werden verworfen)"
                        >
                            <Save className="w-3.5 h-3.5" />
                            Als Vorlage speichern
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setEssentialOnly((v) => !v)}
                        className={`${chipBase} ${essentialOnly ? chipActive : chipInactive}`}
                        data-testid="essential-only-toggle"
                        title="Optionale Felder ausblenden — nur Pflichtfelder anzeigen"
                    >
                        <ListChecks className="w-3.5 h-3.5" />
                        {essentialOnly ? "Nur Pflichtfelder · An" : "Nur Pflichtfelder"}
                    </button>
                </div>

                {draftRestoreCandidate && (
                    <div className="flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-300 rounded-sm px-2.5 py-1.5" data-testid="draft-restore-banner">
                        <RotateCcw className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                        <span className="text-[11px] font-bold text-amber-900">
                            Entwurf vom {draftRestoreCandidate.savedAt.toLocaleString("de-DE", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}
                        </span>
                        <button
                            type="button"
                            onClick={restoreDraft}
                            className="inline-flex items-center gap-1 h-7 px-2 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider text-[10px]"
                            data-testid="draft-restore-btn"
                        >
                            <Copy className="w-3 h-3" /> Wiederherstellen
                        </button>
                        <button
                            type="button"
                            onClick={discardDraft}
                            className="inline-flex items-center gap-1 h-7 px-2 border border-amber-300 text-amber-900 hover:bg-amber-100 font-bold rounded-sm uppercase tracking-wider text-[10px]"
                            data-testid="draft-discard-btn"
                            title="Entwurf verwerfen"
                        >
                            <X className="w-3 h-3" /> Verwerfen
                        </button>
                    </div>
                )}
            </div>

            <SaveAsTemplateDialog
                open={tplDialogOpen}
                onOpenChange={setTplDialogOpen}
                form={form}
            />
        </div>
    );
}
